import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Graphic } from 'src/app/models/graphic.model';
import { VariantEvaluationService } from '../VariantEvaluation.service';
import { TrainEvaluationService } from '../TrainEvaluation.service';

@Component({
    selector: 'app-evaluation-graphics',
    templateUrl: './evaluation-graphics.component.html',
    styleUrls: ['./evaluation-graphics.component.scss']
})
export class EvaluationGraphicsComponent implements OnInit, OnDestroy {
    @Input() context: 'layout' | 'train' = 'layout';
    @Input() evaluationService: VariantEvaluationService | TrainEvaluationService;

    selectedGraphics: Set<number> = new Set();

    categories = {
        baggage: 'Baggage stowability',
        seats: 'Seat availability',
        timeNeed: 'Time need for passenger change'
    };

    groupedGraphics: any = {
        baggage: [],
        seats: [],
        timeNeed: {
            cumulative: {
                perDoor: [],
                comparisonDoors: [],
                comparisonSelectedDoors: [],
                comparisonTravelMixes: []
            },
            total: []
        }
    };

    isSelectable(graphic: Graphic): boolean {
        return this.evaluationService.companyGraphics.some(g => g.graphicId === graphic.graphicId) &&
            graphic.transportModes.includes(this.evaluationService.evaluationFrontEndParameter.advancedSettings.transportModeId);
    }

    selectAll(shouldSelect: boolean, category: string): void {
        if (category === 'all') {
            this.selectAll(shouldSelect, 'baggage');
            this.selectAll(shouldSelect, 'seats');
            this.selectAll(shouldSelect, 'timeNeed');
            return;
        }

        const graphics = this.getGraphicsForCategory(category);
        graphics.forEach((graphic: Graphic) => {
            this.updateSelection(shouldSelect, graphic);
        });
    }

    hasSelectable(category: string): boolean {
        let graphics = category === 'all'
            ? [
                ...this.getGraphicsForCategory('baggage'),
                ...this.getGraphicsForCategory('seats'),
                ...this.getGraphicsForCategory('timeNeed')
            ]
            : this.getGraphicsForCategory(category);

        return graphics.some(graphic => this.isSelectable(graphic));
    }

    private flattenTimeNeed(timeNeed: any): Graphic[] {
        return [
            ...timeNeed.cumulative.perDoor,
            ...timeNeed.cumulative.comparisonDoors,
            ...timeNeed.cumulative.comparisonSelectedDoors,
            ...timeNeed.cumulative.comparisonTravelMixes,
            ...timeNeed.total
        ];
    }

    private updateSelection(shouldSelect: boolean, graphic: Graphic): void {
        if (this.isSelectable(graphic)) {
            if (shouldSelect) {
                this.selectedGraphics.add(graphic.graphicId);
            } else {
                this.selectedGraphics.delete(graphic.graphicId);
            }

            this.evaluationService.syncEvaluationGraphics([...this.selectedGraphics]);
        }
    }

    ngOnInit(): void {
        const isLayoutContext = this.context === 'layout';
        const isTrainContext = this.context === 'train';

        this.groupedGraphics = {
            baggage: this.evaluationService.allGraphics.filter(g =>
                g.categoryId === 1 &&
                ((isLayoutContext && g.layoutName !== null) || isTrainContext)
            ),
            seats: this.evaluationService.allGraphics.filter(g =>
                g.categoryId === 2 &&
                ((isLayoutContext && g.layoutName !== null) || isTrainContext)
            ),
            timeNeed: {
                cumulative: {
                    perDoor: this.evaluationService.allGraphics.filter(g =>
                        g.graphicId === 9 &&
                        ((isLayoutContext && g.layoutName !== null) || isTrainContext)
                    ),
                    comparisonDoors: this.evaluationService.allGraphics.filter(g =>
                        [10, 11, 12].includes(g.graphicId) &&
                        ((isLayoutContext && g.layoutName !== null) || isTrainContext)
                    ),
                    comparisonSelectedDoors: this.evaluationService.allGraphics.filter(g =>
                        [13, 14, 15].includes(g.graphicId) &&
                        ((isLayoutContext && g.layoutName !== null) || isTrainContext)
                    ),
                    comparisonTravelMixes: this.evaluationService.allGraphics.filter(g =>
                        [16, 17, 18].includes(g.graphicId) &&
                        ((isLayoutContext && g.layoutName !== null) || isTrainContext)
                    )
                },
                total: this.evaluationService.allGraphics.filter(g =>
                    g.categoryId === 3 &&
                    [19, 20, 21].includes(g.graphicId) &&
                    ((isLayoutContext && g.layoutName !== null) || isTrainContext)
                )
            }
        };
    }

    toggleSelection(graphicId: number): void {
        if (this.selectedGraphics.has(graphicId)) {
            this.selectedGraphics.delete(graphicId);
        } else {
            this.selectedGraphics.add(graphicId);
        }
        this.evaluationService.syncEvaluationGraphics([...this.selectedGraphics]);
    }

    isSelected(graphicId: number): boolean {
        return this.selectedGraphics.has(graphicId);
    }

    getDisabledReason(graphic: Graphic): string | null {
        const isAssigned = this.evaluationService.companyGraphics.some(g => g.graphicId === graphic.graphicId);
        const transportCompatible = graphic.transportModes.includes(this.evaluationService.evaluationFrontEndParameter.advancedSettings.transportModeId);

        if (!transportCompatible) {
            return 'Not available for the current transport mode';
        }
        if (!isAssigned) {
            return 'Not included in your licence';
        }
        return null;
    }


    isCategorySelected(category: string): boolean {
        const graphics = this.getGraphicsForCategory(category);
        return graphics.every((graphic: Graphic) => this.selectedGraphics.has(graphic.graphicId));
    }

    isCategoryIndeterminate(category: string): boolean {
        const graphics = this.getGraphicsForCategory(category);
        const selectedCount = graphics.filter((graphic: Graphic) => this.selectedGraphics.has(graphic.graphicId)).length;
        return selectedCount > 0 && selectedCount < graphics.length;
    }

    areAllSelected(): boolean {
        return Object.keys(this.groupedGraphics)
            .every(category => this.isCategorySelected(category));
    }

    isAllIndeterminate(): boolean {
        const selectedCount = Object.keys(this.groupedGraphics)
            .filter(category => this.isCategorySelected(category))
            .length;

        return selectedCount > 0 && selectedCount < Object.keys(this.groupedGraphics).length;
    }

    private getGraphicsForCategory(category: string): Graphic[] {
        if (category === 'timeNeed') {
            return this.flattenTimeNeed(this.groupedGraphics.timeNeed);
        } else {
            return Array.isArray(this.groupedGraphics[category]) ? this.groupedGraphics[category] : [];
        }
    }

    ngOnDestroy(): void {
    }
}
